import { useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';

import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import {
  CenteredModal,
  Chip,
  OfferList,
  ParticipationStatusLabel,
} from 'components';
import {
  Feature,
  LinkedinVerificationStatus,
  Participation,
  useAddOfferToPlan,
  useCapabilitiesFirstOffer,
  useIsAdmin,
  usePanel,
  useParticipantReferralLink,
  useParticipationParticipant,
  usePlan,
  usePlanOffers,
  useSession,
  useShortUrl,
} from '@app/api';
import { getFormattedPhoneNumber } from 'lib/participantUtils';
import { ParticipationDateChip } from '../ParticipationDateChip';
import { EditableParticipationTagList } from './EditableParticipationTagList';
import { ParticipationSourceChips } from '../ParticipationSourceChips';
import { IconChip, TooltipText } from '@app/core';
import { config } from 'config';
import { LabelWithVerifiedIcon } from 'pages/Dashboard/Sessions/Details/components/LabelWithVerifiedIcon';

const useStyles = createUseStyles((theme: Theme) => ({
  label: {
    ...theme.fonts.label,
    color: theme.colors.inkSecondary,
  },
  adminLabel: {
    ...theme.fonts.label,
    color: theme.colors.purpleAccessible,
  },
  adminIcon: {
    fontSize: theme.iconSize.small,
    fontWeight: 400,
    marginRight: theme.marginBase / 2,
  },
  value: {
    ...theme.fonts.caption,
    marginTop: theme.marginBase,
    color: theme.colors.inkPrimary,

    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  email: {
    lineBreak: 'anywhere',
  },
  chipsLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',

    marginRight: -theme.marginBase,
  },
  chipInLine: {
    marginTop: theme.marginBase,
    marginRight: theme.marginBase,
  },
  itemGroup: {
    marginTop: theme.marginBase * 3,
  },
  link: {
    ...theme.fonts.caption,
    textDecoration: 'underline',
    color: theme.colors.blueAccessible,
  },
  itemsOneLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.marginBase * 2,
  },
  oneLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  iconCheck: {
    fontSize: theme.iconSize.regular,
    color: theme.colors.greenAccessible,
  },
  iconClose: {
    fontSize: theme.iconSize.regular,
    color: theme.colors.redAccessible,
  },
  tooltip: {
    width: 'fit-content',
  },
  iconEye: {
    marginLeft: theme.marginBase,
    cursor: 'pointer',
  },
  tooltips: {
    display: 'flex',
  },
}));

interface Props {
  participation: Participation;
}

export const DetailsModalLeftPane = ({ participation }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const { data: participant, refetch } =
    useParticipationParticipant(participation);
  const { data: session } = useSession(participation.sessionId);
  const { data: plan } = usePlan(session?.planId);
  const status = participation.status;
  const { data: panel } = usePanel(participant?.panelId, {
    retry: false,
  });
  const { mutateAsync: updateSupportOffer } = useAddOfferToPlan(
    session?.planId,
  );
  const [upsellModalOpened, setOpenUpsellModal] = useState(false);
  const earlyPhoneOffer = useCapabilitiesFirstOffer(
    Feature.EARLY_PHONE_DISPLAY,
  );
  const { data: planOffer } = usePlanOffers(session?.planId);

  const referralLink = useParticipantReferralLink(
    participation.participantId,
    participation.sessionId,
  );

  const clientHasEarlyPhoneDisplay = !!planOffer?.find(
    (offer) => offer.id === earlyPhoneOffer?.id,
  );

  const isAdmin = useIsAdmin();
  const shortUrl = useShortUrl();
  const shortLink = `${shortUrl}/p/${participation.trackingId}`;

  return (
    <>
      <div className={classes.itemsOneLine}>
        <div className={classes.itemGroup}>
          <div className={classes.label}>
            <Trans i18nKey="participants.display.source" />
          </div>
          <div className={classes.value}>
            <ParticipationSourceChips participation={participation} />
            {panel && panel.name && (
              <Chip color="blue">{panel && panel.name}</Chip>
            )}
          </div>
        </div>

        <div className={classes.itemGroup}>
          <div className={classes.label}>
            <Trans i18nKey="sessions.details.monitoring.statusLabel" />
          </div>
          <div className={classes.chipsLine}>
            <ParticipationStatusLabel
              status={status}
              className={classes.chipInLine}
              sessionId={session?.id}
            />
            <ParticipationDateChip
              participation={participation}
              className={classes.chipInLine}
            />
          </div>
        </div>
      </div>

      {participant && (
        <div className={classes.itemGroup}>
          <LabelWithVerifiedIcon
            verified={participant.emailValidated}
            label={t('participants.display.email')}
          />
          <div className={classnames(classes.value, classes.email)}>
            {participant.email || (
              <TooltipText title={t('participants.tooltip.email')}>
                <div className={classes.tooltip}>
                  <IconChip
                    color="ink"
                    variant="std"
                    label="hidden"
                    icon="ri-lock-2-line"
                    overGray
                  />
                </div>
              </TooltipText>
            )}
          </div>
        </div>
      )}

      {participant && (
        <div className={classes.itemGroup}>
          <div className={classes.label}>
            <Trans title={t('participants.display.phoneNumber')} />
          </div>
          <div className={classes.value}>
            {getFormattedPhoneNumber(participant.phoneNumber) || (
              <div className={classes.tooltips}>
                <div className={classes.tooltip}>
                  <IconChip
                    color="ink"
                    variant="std"
                    label="hidden"
                    icon="ri-lock-2-line"
                    overGray
                  />
                </div>
                <TooltipText title={t('participants.tooltip.phoneNumber')}>
                  <div
                    onClick={() => {
                      if (!clientHasEarlyPhoneDisplay) {
                        setOpenUpsellModal(true);
                      }
                    }}
                  >
                    <IconChip
                      className={classes.iconEye}
                      icon="ri-eye-line"
                      color="blue"
                    />
                  </div>
                </TooltipText>
              </div>
            )}
          </div>
        </div>
      )}
      {earlyPhoneOffer && (
        <CenteredModal
          title={t('participants.tooltip.modalTitle')}
          isOpen={upsellModalOpened}
          onRequestClose={() => {
            setOpenUpsellModal(false);
          }}
          xlarge
        >
          <OfferList
            offers={[earlyPhoneOffer]}
            onClick={async () => {
              await updateSupportOffer({ offerId: earlyPhoneOffer.id });
              await refetch();
              setOpenUpsellModal(false);
            }}
          />
        </CenteredModal>
      )}

      {(participant?.linkedinProfileUrl ||
        participant?.linkedinVerificationStatus) && (
        <div className={classes.itemGroup}>
          <LabelWithVerifiedIcon
            verified={
              participant.linkedinVerificationStatus ===
              LinkedinVerificationStatus.VERIFIED
            }
            label={t('participants.display.linkedIn')}
          />
          <div className={classes.value}>
            <a
              className={classes.link}
              href={participant.linkedinProfileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {participant.linkedinProfileUrl}
            </a>
          </div>
        </div>
      )}

      {participation.subscriptionsCount > 1 && (
        <div className={classes.itemGroup}>
          <div className={classes.label}>
            <Trans i18nKey="sessions.details.monitoring.subscriptionsCount" />
          </div>
          <div className={classes.value}>
            {participation.subscriptionsCount}
          </div>
        </div>
      )}

      {isAdmin && (
        <div className={classes.itemGroup}>
          <div className={classes.adminLabel}>
            <i className={classnames('ri-admin-line', classes.adminIcon)} />
            <Trans i18nKey="sessions.details.monitoring.shortLink" />
          </div>
          <div className={classes.value}>
            <a
              className={classes.link}
              href={shortLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {shortLink}
            </a>
          </div>
        </div>
      )}

      {isAdmin && referralLink && plan?.referralActivated && (
        <div className={classes.itemGroup}>
          <div className={classes.adminLabel}>
            <i className={classnames('ri-admin-line', classes.adminIcon)} />
            Lien de parainaige
          </div>
          <div className={classes.value}>
            <a
              className={classes.link}
              href={shortLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {referralLink}
            </a>
          </div>
        </div>
      )}

      {isAdmin && (
        <div className={classes.itemGroup}>
          <div className={classes.adminLabel}>
            <i className={classnames('ri-admin-line', classes.adminIcon)} />
            <Trans i18nKey="sessions.details.monitoring.forestLink" />
          </div>
          <div className={classes.value}>
            <a
              className={classes.link}
              href={`${config.FOREST_URL_PREFIX}/participation/index/record/participation/${participation.id}/details`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Trans i18nKey="sessions.details.monitoring.forestLink" />
            </a>
          </div>
        </div>
      )}

      <div className={classes.itemGroup}>
        <div className={classes.label}>
          <Trans i18nKey="sessions.details.monitoring.tags.title" />
        </div>
        <div className={classes.value}>
          <EditableParticipationTagList participation={participation} />
        </div>
      </div>
    </>
  );
};
